import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OverviewTab from "./components/OverviewTab";
import UsersTab from "./components/UsersTab";
import CompaniesTab from "./components/CompaniesTab";
import SubscriptionsTab from "./components/SubscriptionsTab";
import DataReviewTab from "./components/DataReviewTab";
import { emptyUserForm } from "./adminShared";

const emptyAccountForm = {
    name: "",
    domain: "",
    is_active: true,
};

const emptySubscriptionForm = {
    subscription_type: "company",
    user_id: "",
    account_id: "",
    expires_at: "",
    is_active: true,
};

const PENDING_WINDOW_GLASS_PAGE_SIZE = 100;
const PIPELINE_RUNS_PAGE_SIZE = 10;
const MANAGEMENT_PAGE_SIZE = 10;

const inputClass =
    "h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-200";
const checkboxClass =
    "h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-300";
const primaryButtonClass =
    "inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60";
const secondaryButtonClass =
    "inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50";
const mutedButtonClass =
    "inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50";
const dangerButtonClass =
    "inline-flex h-10 items-center justify-center rounded-lg bg-rose-600 px-3 text-sm font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60";

const tabs = [
    { id: "overview", label: "Overview" },
    { id: "users", label: "Users" },
    { id: "companies", label: "Companies" },
    { id: "subscriptions", label: "Subscriptions" },
    { id: "data-review", label: "Data Review" },
];

const toBool = (value) => Boolean(value);

const toLocalInputValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return typeof value === "string" ? value.slice(0, 16) : "";
    }

    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}`;
};

const isSubscriptionCurrentlyActive = (sub) => {
    if (!sub?.is_active || !sub?.expires_at) return false;
    const expiry = new Date(sub.expires_at);
    if (Number.isNaN(expiry.getTime())) return false;
    return expiry > new Date();
};

const buildUserDrafts = (rows) =>
    Object.fromEntries(
        rows.map((user) => [
            user.user_id,
            {
                email: user.email || "",
                username: user.username || "",
                phone_number: user.phone_number || "",
                role: user.role || "user",
                is_active: toBool(user.is_active),
                email_verified: toBool(user.email_verified),
            },
        ])
    );

const buildAccountDrafts = (rows) =>
    Object.fromEntries(
        rows.map((account) => [
            account.account_id,
            {
                name: account.name || "",
                is_active: toBool(account.is_active),
            },
        ])
    );

const buildDomainDrafts = (rows) => {
    const entries = [];
    rows.forEach((account) => {
        (account.domains || []).forEach((domain) => {
            entries.push([
                domain.account_domain_id,
                {
                    domain: domain.domain || "",
                    is_active: toBool(domain.is_active),
                },
            ]);
        });
    });
    return Object.fromEntries(entries);
};

const buildSubscriptionDrafts = (rows) =>
    Object.fromEntries(
        rows.map((sub) => [
            sub.subscription_id,
            {
                subscription_type: sub.subscription_type || "company",
                user_id: sub.user_id ? String(sub.user_id) : "",
                account_id: sub.account_id ? String(sub.account_id) : "",
                expires_at: toLocalInputValue(sub.expires_at),
                is_active: toBool(sub.is_active),
            },
        ])
    );

const ModalDialog = ({ open, title, onClose, children }) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
            <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
                <div className="flex items-center justify-between gap-4">
                    <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
                    <button
                        type="button"
                        className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                        onClick={onClose}
                    >
                        ❌️
                    </button>
                </div>
                <div className="mt-5 space-y-4 text-sm text-slate-700">{children}</div>
            </div>
        </div>
    );
};

const ConfirmationDialog = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel = "Cancel",
    onConfirm,
    onClose,
    loading = false,
}) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-xl font-semibold text-slate-900 text-left">{title}</h3>
                        <p className="mt-2 text-sm text-slate-600 text-left">{message}</p>
                    </div>
                    <button
                        type="button"
                        className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                        onClick={onClose}
                        disabled={loading}
                        aria-label="Close confirmation dialog"
                    >
                        ❌️
                    </button>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        className={secondaryButtonClass}
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className={dangerButtonClass}
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {loading ? "Working..." : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminPage = () => {
    const [activeTab, setActiveTab] = useState("overview");

    const [summary, setSummary] = useState(null);
    const [users, setUsers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [usersPagination, setUsersPagination] = useState({
        page: 1,
        page_size: MANAGEMENT_PAGE_SIZE,
        total_count: 0,
        total_pages: 1,
    });
    const [accountsPagination, setAccountsPagination] = useState({
        page: 1,
        page_size: MANAGEMENT_PAGE_SIZE,
        total_count: 0,
        total_pages: 1,
    });
    const [subscriptionsPagination, setSubscriptionsPagination] = useState({
        page: 1,
        page_size: MANAGEMENT_PAGE_SIZE,
        total_count: 0,
        total_pages: 1,
    });
    const [pendingWindowGlass, setPendingWindowGlass] = useState([]);
    const [pendingWindowGlassPagination, setPendingWindowGlassPagination] = useState({
        page: 1,
        page_size: PENDING_WINDOW_GLASS_PAGE_SIZE,
        total_count: 0,
        total_pages: 1,
    });
    const [pendingWindowGlassPage, setPendingWindowGlassPage] = useState(1);
    const [pipelineRunsPagination, setPipelineRunsPagination] = useState({
        page: 1,
        page_size: PIPELINE_RUNS_PAGE_SIZE,
        total_count: 0,
        total_pages: 1,
    });
    const [pipelineRunsPage, setPipelineRunsPage] = useState(1);
    const [selectedPendingIds, setSelectedPendingIds] = useState([]);
    const [pipelineRuns, setPipelineRuns] = useState([]);
    const [pipelineState, setPipelineState] = useState({ glass_last_id: 0 });
    const [pipelineStatus, setPipelineStatus] = useState({
        is_running: false,
        stop_requested: false,
        current_run: null,
    });
    const [nextGlassIdInput, setNextGlassIdInput] = useState("");

    const [userDrafts, setUserDrafts] = useState({});
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [accountDrafts, setAccountDrafts] = useState({});
    const [domainDrafts, setDomainDrafts] = useState({});
    const [subscriptionDrafts, setSubscriptionDrafts] = useState({});

    const [newDomainInputs, setNewDomainInputs] = useState({});
    const [accountForm, setAccountForm] = useState(emptyAccountForm);
    const [subscriptionForm, setSubscriptionForm] = useState(emptySubscriptionForm);

    const [userSearch, setUserSearch] = useState("");
    const [accountSearch, setAccountSearch] = useState("");
    const [subscriptionSearch, setSubscriptionSearch] = useState("");

    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [firstLoadComplete, setFirstLoadComplete] = useState(false);

    const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [confirmationDialog, setConfirmationDialog] = useState(null);
    const usersPageRef = useRef(1);
    const accountsPageRef = useRef(1);
    const subscriptionsPageRef = useRef(1);
    const pendingWindowGlassPageRef = useRef(pendingWindowGlassPage);
    const pipelineRunsPageRef = useRef(pipelineRunsPage);

    useEffect(() => {
        usersPageRef.current = usersPagination.page;
    }, [usersPagination.page]);

    useEffect(() => {
        accountsPageRef.current = accountsPagination.page;
    }, [accountsPagination.page]);

    useEffect(() => {
        subscriptionsPageRef.current = subscriptionsPagination.page;
    }, [subscriptionsPagination.page]);

    useEffect(() => {
        pendingWindowGlassPageRef.current = pendingWindowGlassPage;
    }, [pendingWindowGlassPage]);

    useEffect(() => {
        pipelineRunsPageRef.current = pipelineRunsPage;
    }, [pipelineRunsPage]);

    const refreshPendingWindowGlass = useCallback(async (silent = false) => {
        try {
            const page = pendingWindowGlassPageRef.current;
            const pendingRes = await axiosInstance.get(
                `/admin/review/window_glass?page=${page}&page_size=${PENDING_WINDOW_GLASS_PAGE_SIZE}`
            );
            const pendingData = pendingRes.data || {};
            setPendingWindowGlass(pendingData.items || []);
            setPendingWindowGlassPagination(
                pendingData.pagination || {
                    page,
                    page_size: PENDING_WINDOW_GLASS_PAGE_SIZE,
                    total_count: 0,
                    total_pages: 1,
                }
            );
            setSelectedPendingIds([]);
            if (!silent) {
                toast.success("Window glass table refreshed.");
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to refresh window glass review table."
            );
        }
    }, []);

    const refreshPipelinePanel = useCallback(async () => {
        try {
            const page = pipelineRunsPageRef.current;
            const [runsRes, stateRes, statusRes] = await Promise.all([
                axiosInstance.get(
                    `/admin/pipeline/runs?page=${page}&page_size=${PIPELINE_RUNS_PAGE_SIZE}`
                ),
                axiosInstance.get("/admin/pipeline/state"),
                axiosInstance.get("/admin/pipeline/status"),
            ]);
            const runsData = runsRes.data || {};
            setPipelineRuns(runsData.items || []);
            const nextRunsPagination = runsData.pagination || {
                page,
                page_size: PIPELINE_RUNS_PAGE_SIZE,
                total_count: 0,
                total_pages: 1,
            };
            setPipelineRunsPagination(nextRunsPagination);
            setPipelineRunsPage(nextRunsPagination.page || page);
            const nextState = stateRes.data || { glass_last_id: 0 };
            setPipelineState(nextState);
            setNextGlassIdInput((prev) => (prev === "" ? String(nextState.glass_last_id ?? 0) : prev));
            const nextStatus =
                statusRes.data || {
                    is_running: false,
                    stop_requested: false,
                    current_run: null,
                };
            setPipelineStatus(nextStatus);
            if (!nextStatus.is_running) {
                await refreshPendingWindowGlass(true);
            }
        } catch (_err) {
            // Keep silent for background polling to avoid toast spam.
        }
    }, [refreshPendingWindowGlass]);

    const loadAll = useCallback(async (options = {}) => {
        const {
            pendingPage = pendingWindowGlassPageRef.current,
            pipelinePage = pipelineRunsPageRef.current,
            usersPageParam = usersPageRef.current,
            accountsPageParam = accountsPageRef.current,
            subscriptionsPageParam = subscriptionsPageRef.current,
        } = options;

        try {
            setLoading(true);

            const [summaryRes, usersRes, accountsRes, subscriptionsRes, pendingRes, runsRes, stateRes, statusRes] = await Promise.all([
                axiosInstance.get("/admin/summary"),
                axiosInstance.get(`/admin/users?page=${usersPageParam}&page_size=${MANAGEMENT_PAGE_SIZE}`),
                axiosInstance.get(`/admin/accounts?page=${accountsPageParam}&page_size=${MANAGEMENT_PAGE_SIZE}`),
                axiosInstance.get(`/admin/subscriptions?page=${subscriptionsPageParam}&page_size=${MANAGEMENT_PAGE_SIZE}`),
                axiosInstance.get(`/admin/review/window_glass?page=${pendingPage}&page_size=${PENDING_WINDOW_GLASS_PAGE_SIZE}`),
                axiosInstance.get(
                    `/admin/pipeline/runs?page=${pipelinePage}&page_size=${PIPELINE_RUNS_PAGE_SIZE}`
                ),
                axiosInstance.get("/admin/pipeline/state"),
                axiosInstance.get("/admin/pipeline/status"),
            ]);

            setSummary(summaryRes.data || null);
            const usersData = usersRes.data || {};
            const accountsData = accountsRes.data || {};
            const subscriptionsData = subscriptionsRes.data || {};

            setUsers(usersData.items || []);
            setUsersPagination(
                usersData.pagination || {
                    page: usersPageParam,
                    page_size: MANAGEMENT_PAGE_SIZE,
                    total_count: 0,
                    total_pages: 1,
                }
            );
            setAccounts(accountsData.items || []);
            setAccountsPagination(
                accountsData.pagination || {
                    page: accountsPageParam,
                    page_size: MANAGEMENT_PAGE_SIZE,
                    total_count: 0,
                    total_pages: 1,
                }
            );
            setSubscriptions(subscriptionsData.items || []);
            setSubscriptionsPagination(
                subscriptionsData.pagination || {
                    page: subscriptionsPageParam,
                    page_size: MANAGEMENT_PAGE_SIZE,
                    total_count: 0,
                    total_pages: 1,
                }
            );
            const pendingData = pendingRes.data || {};
            setPendingWindowGlass(pendingData.items || []);
            setPendingWindowGlassPagination(
                pendingData.pagination || {
                    page: pendingPage,
                    page_size: PENDING_WINDOW_GLASS_PAGE_SIZE,
                    total_count: 0,
                    total_pages: 1,
                }
            );
            const runsData = runsRes.data || {};
            setPipelineRuns(runsData.items || []);
            const nextRunsPagination = runsData.pagination || {
                page: pipelinePage,
                page_size: PIPELINE_RUNS_PAGE_SIZE,
                total_count: 0,
                total_pages: 1,
            };
            setPipelineRunsPagination(nextRunsPagination);
            setPipelineRunsPage(nextRunsPagination.page || pipelinePage);
            const nextState = stateRes.data || { glass_last_id: 0 };
            setPipelineState(nextState);
            setPipelineStatus(
                statusRes.data || {
                    is_running: false,
                    stop_requested: false,
                    current_run: null,
                }
            );
            setNextGlassIdInput(String(nextState.glass_last_id ?? 0));
            setSelectedPendingIds([]);

            setUserDrafts(buildUserDrafts(usersData.items || []));
            setAccountDrafts(buildAccountDrafts(accountsData.items || []));
            setDomainDrafts(buildDomainDrafts(accountsData.items || []));
            setSubscriptionDrafts(buildSubscriptionDrafts(subscriptionsData.items || []));
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to load admin data."
            );
        } finally {
            setLoading(false);
            setFirstLoadComplete((prev) => (prev ? prev : true));
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    useEffect(() => {
        const timer = setInterval(() => {
            refreshPipelinePanel();
        }, 7000);
        return () => clearInterval(timer);
    }, [refreshPipelinePanel]);

    const runAction = async (action, successMessage, afterSuccess = null) => {
        try {
            setWorking(true);
            await action();
            toast.success(successMessage);
            if (afterSuccess) {
                await afterSuccess();
            } else {
                await loadAll();
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Something went wrong."
            );
        } finally {
            setWorking(false);
        }
    };

    const saveUser = async (userId) => {
        const draft = userDrafts[userId];
        await runAction(async () => {
            await axiosInstance.patch(`/admin/users/${userId}`, {
                email: draft.email.trim(),
                username: draft.username.trim(),
                phone_number: draft.phone_number.trim(),
                role: draft.role,
                is_active: draft.is_active,
                email_verified: draft.email_verified,
            });
        }, "User updated.");
    };

    const createUser = async () => {
        const email = userForm.email.trim();
        const username = userForm.username.trim();
        const phoneNumber = userForm.phone_number.trim();

        if (!email) {
            toast.error("Email is required.");
            return;
        }

        await runAction(async () => {
            await axiosInstance.post("/admin/users", {
                email,
                username,
                phone_number: phoneNumber,
                role: userForm.role,
                email_verified: userForm.email_verified,
                is_active: userForm.is_active,
            });
            setUserForm(emptyUserForm);
            setUserDialogOpen(false);
        }, "User created.", async () => {
            await loadAll({ usersPage: 1 });
        });
    };

    const createAccount = async () => {
        await runAction(async () => {
            await axiosInstance.post("/admin/accounts", {
                name: accountForm.name.trim(),
                domain: accountForm.domain.trim(),
                is_active: accountForm.is_active,
            });
            setAccountForm(emptyAccountForm);
            setActiveTab("companies");
        }, "Company created.", async () => {
            await loadAll({ accountsPage: 1 });
        });
        setCompanyDialogOpen(false);
    };

    const saveAccount = async (accountId) => {
        const draft = accountDrafts[accountId];
        await runAction(async () => {
            await axiosInstance.patch(`/admin/accounts/${accountId}`, {
                name: draft.name.trim(),
                is_active: draft.is_active,
            });
        }, "Company updated.");
    };

    const createAccountDomain = async (accountId) => {
        const domain = (newDomainInputs[accountId] || "").trim();
        if (!domain) {
            toast.error("Domain is required.");
            return;
        }

        await runAction(async () => {
            await axiosInstance.post("/admin/account-domains", {
                account_id: Number(accountId),
                domain,
            });
            setNewDomainInputs((prev) => ({ ...prev, [accountId]: "" }));
        }, "Domain added.");
    };

    const saveAccountDomain = async (accountDomainId) => {
        const draft = domainDrafts[accountDomainId];
        await runAction(async () => {
            await axiosInstance.patch(`/admin/account-domains/${accountDomainId}`, {
                domain: draft.domain.trim(),
                is_active: draft.is_active,
            });
        }, "Domain updated.");
    };

    const createSubscription = async () => {
        await runAction(async () => {
            const payload = {
                subscription_type: subscriptionForm.subscription_type,
                expires_at: subscriptionForm.expires_at,
                is_active: subscriptionForm.is_active,
                user_id:
                    subscriptionForm.subscription_type === "individual" && subscriptionForm.user_id
                        ? Number(subscriptionForm.user_id)
                        : null,
                account_id:
                    subscriptionForm.subscription_type === "company" && subscriptionForm.account_id
                        ? Number(subscriptionForm.account_id)
                        : null,
            };

            await axiosInstance.post("/admin/subscriptions", payload);
            setSubscriptionForm(emptySubscriptionForm);
            setActiveTab("subscriptions");
        }, "Subscription created.", async () => {
            await loadAll({ subscriptionsPage: 1 });
        });
        setSubscriptionDialogOpen(false);
    };

    const setSubscriptionOneYearAhead = () => {
        const target = new Date();
        target.setFullYear(target.getFullYear() + 1);
        setSubscriptionForm((prev) => ({
            ...prev,
            expires_at: toLocalInputValue(target),
        }));
    };

    const saveSubscription = async (subscriptionId) => {
        const draft = subscriptionDrafts[subscriptionId];
        await runAction(async () => {
            await axiosInstance.patch(`/admin/subscriptions/${subscriptionId}`, {
                subscription_type: draft.subscription_type,
                user_id:
                    draft.subscription_type === "individual" && draft.user_id
                        ? Number(draft.user_id)
                        : null,
                account_id:
                    draft.subscription_type === "company" && draft.account_id
                        ? Number(draft.account_id)
                        : null,
                expires_at: draft.expires_at,
                is_active: draft.is_active,
            });
        }, "Subscription updated.");
    };

    const goToUsersPage = async (nextPage) => {
        const clampedPage = Math.max(1, Math.min(nextPage, usersPagination.total_pages || 1));
        if (clampedPage === usersPagination.page) return;
        await loadAll({ usersPage: clampedPage });
    };

    const goToAccountsPage = async (nextPage) => {
        const clampedPage = Math.max(1, Math.min(nextPage, accountsPagination.total_pages || 1));
        if (clampedPage === accountsPagination.page) return;
        await loadAll({ accountsPage: clampedPage });
    };

    const goToSubscriptionsPage = async (nextPage) => {
        const clampedPage = Math.max(1, Math.min(nextPage, subscriptionsPagination.total_pages || 1));
        if (clampedPage === subscriptionsPagination.page) return;
        await loadAll({ subscriptionsPage: clampedPage });
    };

    const goToPipelineRunsPage = async (nextPage) => {
        const clampedPage = Math.max(
            1,
            Math.min(nextPage, pipelineRunsPagination.total_pages || 1)
        );
        if (clampedPage === pipelineRunsPage) {
            return;
        }
        setPipelineRunsPage(clampedPage);
        try {
            const runsRes = await axiosInstance.get(
                `/admin/pipeline/runs?page=${clampedPage}&page_size=${PIPELINE_RUNS_PAGE_SIZE}`
            );
            const runsData = runsRes.data || {};
            const nextRunsPagination = runsData.pagination || {
                page: clampedPage,
                page_size: PIPELINE_RUNS_PAGE_SIZE,
                total_count: 0,
                total_pages: 1,
            };

            if ((runsData.items || []).length === 0 && clampedPage > 1 && nextRunsPagination.total_pages < clampedPage) {
                return goToPipelineRunsPage(nextRunsPagination.total_pages);
            }

            setPipelineRuns(runsData.items || []);
            setPipelineRunsPagination(nextRunsPagination);
            setPipelineRunsPage(nextRunsPagination.page || clampedPage);
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to change pipeline log page."
            );
        }
    };

    const togglePendingSelect = (rowId, checked) => {
        setSelectedPendingIds((prev) => {
            if (checked) return Array.from(new Set([...prev, rowId]));
            return prev.filter((id) => id !== rowId);
        });
    };

    const toggleSelectAllPendingWindowGlass = () => {
        const visibleIds = pendingWindowGlass.map((row) => row.window_glass_id);
        const allSelected =
            visibleIds.length > 0 &&
            visibleIds.every((id) => selectedPendingIds.includes(id));

        setSelectedPendingIds(allSelected ? [] : visibleIds);
    };

    const goToPendingWindowGlassPage = async (nextPage) => {
        const clampedPage = Math.max(1, Math.min(nextPage, pendingWindowGlassPagination.total_pages || 1));
        if (clampedPage === pendingWindowGlassPage) {
            return;
        }
        setPendingWindowGlassPage(clampedPage);
        setSelectedPendingIds([]);
        try {
            const pendingRes = await axiosInstance.get(
                `/admin/review/window_glass?page=${clampedPage}&page_size=${PENDING_WINDOW_GLASS_PAGE_SIZE}`
            );
            const pendingData = pendingRes.data || {};
            setPendingWindowGlass(pendingData.items || []);
            setPendingWindowGlassPagination(
                pendingData.pagination || {
                    page: clampedPage,
                    page_size: PENDING_WINDOW_GLASS_PAGE_SIZE,
                    total_count: 0,
                    total_pages: 1,
                }
            );
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to change review page."
            );
        }
    };

    const reviewSingleWindowGlass = async (rowId, status) => {
        await runAction(async () => {
            await axiosInstance.patch(`/admin/review/window_glass/${rowId}`, { status });
        }, `Row ${status}.`);
    };

    const reviewBulkWindowGlass = async (status) => {
        if (!selectedPendingIds.length) {
            toast.error("Select at least one row.");
            return;
        }
        await runAction(async () => {
            await axiosInstance.patch("/admin/review/window_glass/bulk", {
                row_ids: selectedPendingIds,
                status,
            });
        }, `Selected ${status} complete.`);
    };

    const approveAllPendingWindowGlass = async () => {
        await runAction(
            async () => {
                await axiosInstance.patch("/admin/review/window_glass/approve-all");
            },
            "All pending window glass rows approved.",
            async () => {
                setPendingWindowGlassPage(1);
                await loadAll(1);
            }
        );
    };

    const deleteAllPendingWindowGlass = async () => {
        setConfirmationDialog({
            title: "Delete all pending rows?",
            message: "This will permanently remove every pending window glass row and cannot be undone.",
            confirmLabel: "Delete all",
            onConfirm: async () => {
                setConfirmationDialog(null);
                await runAction(
                    async () => {
                        await axiosInstance.delete("/admin/review/window_glass/delete-all-pending");
                    },
                    "All pending window glass rows deleted.",
                    async () => {
                        setPendingWindowGlassPage(1);
                        await loadAll(1);
                    }
                );
            },
        });
    };

    const triggerPipeline = async () => {
        await runAction(async () => {
            await axiosInstance.post("/admin/pipeline/run");
        }, "Pipeline triggered.");
    };

    const deletePipelineRun = async (runId) => {
        setConfirmationDialog({
            title: `Delete pipeline run #${runId}?`,
            message: "This will permanently remove the run record and cannot be undone.",
            confirmLabel: "Delete run",
            onConfirm: async () => {
                setConfirmationDialog(null);
                await runAction(async () => {
                    await axiosInstance.delete(`/admin/pipeline/runs/${runId}`);
                }, "Pipeline run deleted.", async () => {
                    await goToPipelineRunsPage(pipelineRunsPageRef.current);
                });
            },
        });
    };

    const stopPipeline = async () => {
        await runAction(async () => {
            await axiosInstance.post("/admin/pipeline/stop");
        }, "Pipeline stop requested.");
    };

    const updateLastGlassId = async () => {
        await runAction(async () => {
            await axiosInstance.patch("/admin/pipeline/state", {
                glass_last_id: Number(nextGlassIdInput),
            });
        }, "Last glass id updated.");
    };

    const userMap = useMemo(
        () =>
            Object.fromEntries(
                users.map((user) => [
                    user.user_id,
                    user.username
                        ? `${user.username} (${user.email})`
                        : user.email || `User #${user.user_id}`,
                ])
            ),
        [users]
    );

    const accountMap = useMemo(
        () => Object.fromEntries(accounts.map((account) => [account.account_id, account.name])),
        [accounts]
    );

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return users;
        return users.filter((user) =>
            [user.email, user.username, user.phone_number, user.role]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(q))
        );
    }, [users, userSearch]);

    const filteredAccounts = useMemo(() => {
        const q = accountSearch.trim().toLowerCase();
        if (!q) return accounts;
        return accounts.filter((account) => {
            const domains = (account.domains || []).map((d) => d.domain).join(" ");
            return [account.name, domains].some((value) =>
                String(value || "")
                    .toLowerCase()
                    .includes(q)
            );
        });
    }, [accounts, accountSearch]);

    const filteredSubscriptions = useMemo(() => {
        const q = subscriptionSearch.trim().toLowerCase();
        if (!q) return subscriptions;

        return subscriptions.filter((sub) => {
            const pieces = [
                sub.subscription_type,
                userMap[sub.user_id] || "",
                accountMap[sub.account_id] || "",
                sub.user_id,
                sub.account_id,
                sub.expires_at,
            ];
            return pieces.some((value) =>
                String(value || "")
                    .toLowerCase()
                    .includes(q)
            );
        });
    }, [subscriptions, subscriptionSearch, userMap, accountMap]);

    const overviewBarData = useMemo(() => {
        const activeUsers = summary?.active_users ?? users.filter((u) => u.is_active).length;
        const totalUsers = summary?.users ?? users.length;
        const activeAccounts = summary?.active_accounts ?? accounts.filter((a) => a.is_active).length;
        const totalAccounts = summary?.accounts ?? accounts.length;
        const activeSubscriptions =
            summary?.active_subscriptions ?? subscriptions.filter(isSubscriptionCurrentlyActive).length;
        const totalSubscriptions = summary?.subscriptions ?? subscriptions.length;
        return [
            {
                name: "Users",
                total: totalUsers,
                active: activeUsers,
                inactive: Math.max(0, totalUsers - activeUsers),
            },
            {
                name: "Companies",
                total: totalAccounts,
                active: activeAccounts,
                inactive: Math.max(0, totalAccounts - activeAccounts),
            },
            {
                name: "Subscriptions",
                total: totalSubscriptions,
                active: activeSubscriptions,
                inactive: Math.max(0, totalSubscriptions - activeSubscriptions),
            },
        ];
    }, [summary, users, accounts, subscriptions]);

    const subscriptionTypeData = useMemo(() => {
        const companyCount = subscriptions.filter(
            (sub) => sub.subscription_type === "company"
        ).length;
        const individualCount = subscriptions.filter(
            (sub) => sub.subscription_type === "individual"
        ).length;

        return [
            { name: "Company", value: companyCount },
            { name: "Individual", value: individualCount },
        ].filter((item) => item.value > 0);
    }, [subscriptions]);

    const subscriptionHealthData = useMemo(() => {
        const now = new Date();

        let expired = 0;
        let next30 = 0;
        let next90 = 0;
        let later = 0;

        subscriptions.forEach((sub) => {
            if (!sub.expires_at) return;
            const expiry = new Date(sub.expires_at);
            if (Number.isNaN(expiry.getTime())) return;

            const diffDays = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

            if (diffDays < 0) expired += 1;
            else if (diffDays <= 30) next30 += 1;
            else if (diffDays <= 90) next90 += 1;
            else later += 1;
        });

        return [
            { name: "Expired", value: expired },
            { name: "0-30 days", value: next30 },
            { name: "31-90 days", value: next90 },
            { name: "90+ days", value: later },
        ];
    }, [subscriptions]);

    if (loading && !firstLoadComplete) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm">
                    <p className="text-sm text-slate-600">Loading admin data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
            <div className="mx-auto max-w-[140rem] space-y-6">
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 text-left">
                                Manage users, companies, and subscriptions
                            </h1>
                            <p className="mt-2 max-w-2xl text-sm text-slate-600 text-left">
                                Start in <span className="font-medium text-slate-900">Overview</span> for
                                stats, then switch tabs to manage each area separately. This keeps each task
                                clear instead of mixing everything on one screen.
                            </p>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2">
                            <div className="flex flex-wrap gap-2">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`rounded-xl px-4 py-2 text-sm font-medium transition ${activeTab === tab.id
                                            ? "bg-slate-900 text-white"
                                            : "text-slate-600 hover:bg-white hover:text-slate-900"
                                            }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {activeTab === "overview" && (
                    <OverviewTab
                        summary={summary}
                        users={users}
                        accounts={accounts}
                        subscriptions={subscriptions}
                        overviewBarData={overviewBarData}
                        subscriptionTypeData={subscriptionTypeData}
                        subscriptionHealthData={subscriptionHealthData}
                    />
                )}

                {activeTab === "users" && (
                    <UsersTab
                        userSearch={userSearch}
                        setUserSearch={setUserSearch}
                        filteredUsers={filteredUsers}
                        usersPagination={usersPagination}
                        goToUsersPage={goToUsersPage}
                        userDrafts={userDrafts}
                        setUserDrafts={setUserDrafts}
                        working={working}
                        saveUser={saveUser}
                        setUserDialogOpen={setUserDialogOpen}
                    />
                )}

                {activeTab === "companies" && (
                    <CompaniesTab
                        accountSearch={accountSearch}
                        setAccountSearch={setAccountSearch}
                        filteredAccounts={filteredAccounts}
                        accountsPagination={accountsPagination}
                        goToAccountsPage={goToAccountsPage}
                        accountDrafts={accountDrafts}
                        setAccountDrafts={setAccountDrafts}
                        domainDrafts={domainDrafts}
                        setDomainDrafts={setDomainDrafts}
                        newDomainInputs={newDomainInputs}
                        setNewDomainInputs={setNewDomainInputs}
                        working={working}
                        saveAccount={saveAccount}
                        saveAccountDomain={saveAccountDomain}
                        createAccountDomain={createAccountDomain}
                        setCompanyDialogOpen={setCompanyDialogOpen}
                    />
                )}

                {activeTab === "subscriptions" && (
                    <SubscriptionsTab
                        subscriptionSearch={subscriptionSearch}
                        setSubscriptionSearch={setSubscriptionSearch}
                        filteredSubscriptions={filteredSubscriptions}
                        subscriptionsPagination={subscriptionsPagination}
                        goToSubscriptionsPage={goToSubscriptionsPage}
                        subscriptionDrafts={subscriptionDrafts}
                        setSubscriptionDrafts={setSubscriptionDrafts}
                        accounts={accounts}
                        users={users}
                        userMap={userMap}
                        working={working}
                        saveSubscription={saveSubscription}
                        setSubscriptionDialogOpen={setSubscriptionDialogOpen}
                    />
                )}

                {activeTab === "data-review" && (
                    <DataReviewTab
                        working={working}
                        pipelineStatus={pipelineStatus}
                        pipelineState={pipelineState}
                        nextGlassIdInput={nextGlassIdInput}
                        setNextGlassIdInput={setNextGlassIdInput}
                        pipelineRuns={pipelineRuns}
                        pipelineRunsPagination={pipelineRunsPagination}
                        goToPipelineRunsPage={goToPipelineRunsPage}
                        deletePipelineRun={deletePipelineRun}
                        triggerPipeline={triggerPipeline}
                        stopPipeline={stopPipeline}
                        updateLastGlassId={updateLastGlassId}
                        refreshPendingWindowGlass={refreshPendingWindowGlass}
                        approveAllPendingWindowGlass={approveAllPendingWindowGlass}
                        deleteAllPendingWindowGlass={deleteAllPendingWindowGlass}
                        toggleSelectAllPendingWindowGlass={toggleSelectAllPendingWindowGlass}
                        reviewBulkWindowGlass={reviewBulkWindowGlass}
                        reviewSingleWindowGlass={reviewSingleWindowGlass}
                        pendingWindowGlass={pendingWindowGlass}
                        selectedPendingIds={selectedPendingIds}
                        pendingWindowGlassPagination={pendingWindowGlassPagination}
                        goToPendingWindowGlassPage={goToPendingWindowGlassPage}
                        togglePendingSelect={togglePendingSelect}
                    />
                )}
            </div>
            <ModalDialog
                open={userDialogOpen}
                title="Create user"
                onClose={() => {
                    setUserDialogOpen(false);
                    setUserForm(emptyUserForm);
                }}
            >
                <div className="space-y-4 text-left">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                Email <span className="text-rose-600">*</span>
                            </label>
                            <input
                                className={inputClass}
                                value={userForm.email}
                                onChange={(e) =>
                                    setUserForm((prev) => ({ ...prev, email: e.target.value }))
                                }
                                placeholder="name@example.com"
                            />
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Username</label>
                            <input
                                className={inputClass}
                                value={userForm.username}
                                onChange={(e) =>
                                    setUserForm((prev) => ({ ...prev, username: e.target.value }))
                                }
                                placeholder="Optional display name"
                            />
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Phone</label>
                            <input
                                className={inputClass}
                                value={userForm.phone_number}
                                onChange={(e) =>
                                    setUserForm((prev) => ({ ...prev, phone_number: e.target.value }))
                                }
                                placeholder="+61..."
                            />
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                Role <span className="text-rose-600">*</span>
                            </label>
                            <select
                                className={inputClass}
                                value={userForm.role}
                                onChange={(e) =>
                                    setUserForm((prev) => ({ ...prev, role: e.target.value }))
                                }
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2 text-left">
                        <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                className={checkboxClass}
                                aria-label="Email verified"
                                checked={!!userForm.email_verified}
                                onChange={(e) =>
                                    setUserForm((prev) => ({ ...prev, email_verified: e.target.checked }))
                                }
                            />
                            Verified <span className="text-rose-600">*</span>
                        </label>

                        <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                className={checkboxClass}
                                aria-label="User status"
                                checked={!!userForm.is_active}
                                onChange={(e) =>
                                    setUserForm((prev) => ({ ...prev, is_active: e.target.checked }))
                                }
                            />
                            Status <span className="text-rose-600">*</span>
                        </label>
                    </div>

                    <div className="flex flex-wrap justify-end gap-3 text-left">
                        <button
                            type="button"
                            className={secondaryButtonClass}
                            onClick={() => setUserForm(emptyUserForm)}
                            disabled={working}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            className={primaryButtonClass}
                            onClick={createUser}
                            disabled={working}
                        >
                            Create user
                        </button>
                    </div>
                </div>
            </ModalDialog>

            <ModalDialog
                open={companyDialogOpen}
                title="Create company"
                onClose={() => setCompanyDialogOpen(false)}
            >
                <div className="space-y-4 text-left">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Company name</label>
                        <input
                            className={inputClass}
                            value={accountForm.name}
                            onChange={(e) =>
                                setAccountForm((prev) => ({ ...prev, name: e.target.value }))
                            }
                            placeholder="CiiLOCK Engineering Pty Ltd"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Primary domain</label>
                        <input
                            className={inputClass}
                            value={accountForm.domain}
                            onChange={(e) =>
                                setAccountForm((prev) => ({ ...prev, domain: e.target.value }))
                            }
                            placeholder="ciilock.com"
                        />
                    </div>
                    <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            className={checkboxClass}
                            aria-label="Company status"
                            checked={accountForm.is_active}
                            onChange={(e) =>
                                setAccountForm((prev) => ({ ...prev, is_active: e.target.checked }))
                            }
                        />
                        Status
                    </label>
                    <div className="flex justify-end gap-3 text-left">
                        <button
                            className={secondaryButtonClass}
                            type="button"
                            onClick={() => setAccountForm(emptyAccountForm)}
                            disabled={working}
                        >
                            Reset
                        </button>
                        <button
                            className={primaryButtonClass}
                            type="button"
                            onClick={createAccount}
                            disabled={working}
                        >
                            Create company
                        </button>
                    </div>
                </div>
            </ModalDialog>

            <ModalDialog
                open={subscriptionDialogOpen}
                title="Create subscription"
                onClose={() => setSubscriptionDialogOpen(false)}
            >
                <div className="space-y-4 text-left">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Subscription type</label>
                        <select
                            className={inputClass}
                            value={subscriptionForm.subscription_type}
                            onChange={(e) =>
                                setSubscriptionForm((prev) => ({
                                    ...prev,
                                    subscription_type: e.target.value,
                                    user_id: "",
                                    account_id: "",
                                }))
                            }
                        >
                            <option value="company">Company</option>
                            <option value="individual">Individual</option>
                        </select>
                    </div>

                    {subscriptionForm.subscription_type === "company" ? (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Company</label>
                            <select
                                className={inputClass}
                                value={subscriptionForm.account_id}
                                onChange={(e) =>
                                    setSubscriptionForm((prev) => ({
                                        ...prev,
                                        account_id: e.target.value,
                                    }))
                                }
                            >
                                <option value="">Select company</option>
                                {accounts.map((account) => (
                                    <option key={account.account_id} value={account.account_id}>
                                        {account.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">User</label>
                            <select
                                className={inputClass}
                                value={subscriptionForm.user_id}
                                onChange={(e) =>
                                    setSubscriptionForm((prev) => ({
                                        ...prev,
                                        user_id: e.target.value,
                                    }))
                                }
                            >
                                <option value="">Select user</option>
                                {users.map((user) => (
                                    <option key={user.user_id} value={user.user_id}>
                                        {userMap[user.user_id]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Expires at</label>
                        <input
                            type="datetime-local"
                            className={inputClass}
                            value={subscriptionForm.expires_at}
                            onChange={(e) =>
                                setSubscriptionForm((prev) => ({
                                    ...prev,
                                    expires_at: e.target.value,
                                }))
                            }
                        />
                    </div>

                    <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            className={checkboxClass}
                            aria-label="Subscription status"
                            checked={subscriptionForm.is_active}
                            onChange={(e) =>
                                setSubscriptionForm((prev) => ({
                                    ...prev,
                                    is_active: e.target.checked,
                                }))
                            }
                        />
                        Status
                    </label>

                    <div className="flex flex-wrap justify-end gap-3 text-left">
                        <button
                            className={mutedButtonClass}
                            type="button"
                            onClick={setSubscriptionOneYearAhead}
                            disabled={working}
                        >
                            Set 1-year expiry
                        </button>
                        <button
                            className={secondaryButtonClass}
                            type="button"
                            onClick={() => setSubscriptionForm(emptySubscriptionForm)}
                            disabled={working}
                        >
                            Reset
                        </button>
                        <button
                            className={primaryButtonClass}
                            type="button"
                            onClick={createSubscription}
                            disabled={working}
                        >
                            Create subscription
                        </button>
                    </div>
                </div>
            </ModalDialog>
            <ConfirmationDialog
                open={Boolean(confirmationDialog)}
                title={confirmationDialog?.title || ""}
                message={confirmationDialog?.message || ""}
                confirmLabel={confirmationDialog?.confirmLabel || "Confirm"}
                onConfirm={confirmationDialog?.onConfirm || (() => { })}
                onClose={() => setConfirmationDialog(null)}
                loading={working}
            />
            <ToastContainer position="top-right" autoClose={4000} newestOnTop />
        </div>
    );
};

export default AdminPage;

